/**
 * IKWF-History.org CSV layout (1-based columns as in spreadsheet):
 * 1 Event, 2 Date, 5 Division, 6 Winner name, 7 Winner weight (lbs),
 * 9 Winner grade, 10 Winner team, 13 Loser name, 14 Loser weight (lbs),
 * 16 Loser grade, 17 Loser team, 21 Result (Pin → Fall).
 * 22 RanksterImported (optional): ISO-8601 instant written by Rankster after
 * import (e.g. 2026-03-28T15:30:00.000Z). Rows are skipped only when this cell
 * matches that pattern, so other data in column 22 from IKWF exports is ignored.
 */

import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import Elo from "elo-js";
import {
  MATCHES_CANONICAL_COLLECTION,
  computeCanonicalMatchDocId,
  participantAthleteIds,
} from "./canonicalMatches";

const MIN_COLS = 21;

export { MATCHES_CANONICAL_COLLECTION } from "./canonicalMatches";

/** @deprecated Use `computeCanonicalMatchDocId` from `canonicalMatches.js`. */
export async function ikwfCanonicalMatchDocId(
  winnerId,
  loserId,
  matchDateTs,
  matchType
) {
  return computeCanonicalMatchDocId(
    winnerId,
    loserId,
    matchDateTs,
    matchType
  );
}

/** 0-based column index for import marker (spreadsheet column 22). */
export const IKWF_IMPORTED_COL_INDEX = 21;

export function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let i = 0;
  let inQuotes = false;
  while (i < line.length) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cur += c;
      i++;
    } else {
      if (c === '"') {
        inQuotes = true;
        i++;
      } else if (c === ",") {
        out.push(cur.trim());
        cur = "";
        i++;
      } else {
        cur += c;
        i++;
      }
    }
  }
  out.push(cur.trim());
  return out;
}

export function parseIkwfHistoryCsv(text) {
  const lines = String(text || "").split(/\r?\n/);
  const rows = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line || !String(line).trim()) continue;
    rows.push({
      lineIndex: i + 1,
      cells: parseCsvLine(line),
    });
  }
  return rows;
}

/** Looks like Rankster's own marker from buildIkwfHistoryCsvWithImportMarkers (toISOString). */
const RANKSTER_IMPORT_TIMESTAMP_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

/**
 * True only if column 22 contains Rankster's import timestamp (not arbitrary IKWF text).
 */
export function isIkwfRowMarkedImported(cells) {
  if (!cells || cells.length <= IKWF_IMPORTED_COL_INDEX) return false;
  const v = String(cells[IKWF_IMPORTED_COL_INDEX] ?? "").trim();
  if (!v) return false;
  return RANKSTER_IMPORT_TIMESTAMP_RE.test(v);
}

function escapeCsvField(val) {
  const t = String(val ?? "");
  if (/[",\n\r]/.test(t)) return `"${t.replace(/"/g, '""')}"`;
  return t;
}

export function serializeCsvRow(cells) {
  return (cells || []).map(escapeCsvField).join(",");
}

/**
 * Sets column 22 on given 1-based line numbers and returns full file text.
 * Preserves blank lines and unmodified rows from the original export.
 */
export function buildIkwfHistoryCsvWithImportMarkers(
  originalText,
  lineNumbers
) {
  const raw = String(originalText || "");
  const lines = raw.split(/\r?\n/);
  const nl = /\r\n/.test(raw) ? "\r\n" : "\n";
  const mark = new Set(lineNumbers || []);
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNum = i + 1;
    if (!line || !String(line).trim()) {
      out.push(line);
      continue;
    }
    if (!mark.has(lineNum)) {
      out.push(line);
      continue;
    }
    const cells = parseCsvLine(line);
    while (cells.length <= IKWF_IMPORTED_COL_INDEX) cells.push("");
    cells[IKWF_IMPORTED_COL_INDEX] = new Date().toISOString();
    out.push(serializeCsvRow(cells));
  }
  return out.join(nl);
}

export function normalizeDivisionLabel(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function divisionCsvMatchesField(csvDivision, leagueDivisionField) {
  const a = normalizeDivisionLabel(csvDivision);
  const b = normalizeDivisionLabel(leagueDivisionField);
  if (!b) return false;
  return a === b;
}

export function extractIkwfRow(cells) {
  if (!cells || cells.length < MIN_COLS) return null;
  return {
    event: String(cells[0] ?? "").trim(),
    dateRaw: String(cells[1] ?? "").trim(),
    division: String(cells[4] ?? "").trim(),
    winnerName: String(cells[5] ?? "").trim(),
    winnerWeightRaw: String(cells[6] ?? "").trim(),
    winnerGrade: String(cells[8] ?? "").trim(),
    winnerTeam: String(cells[9] ?? "").trim(),
    loserName: String(cells[12] ?? "").trim(),
    loserWeightRaw: String(cells[13] ?? "").trim(),
    loserGrade: String(cells[15] ?? "").trim(),
    loserTeam: String(cells[16] ?? "").trim(),
    resultRaw: String(cells[20] ?? "").trim(),
  };
}

/** First positive number in a string (e.g. "47.8", "47.8 lbs"). */
export function parseIkwfPounds(raw) {
  const s = String(raw || "").trim();
  if (!s) return null;
  const m = s.match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Weight class labels are upper limits (e.g. 47.8 lbs → "49 Pounds" if 46 is too light).
 * Uses the first number in each class label as the limit. Returns the stored Weight string.
 */
export function pickRankWeightClassForPounds(weightclasses, pounds) {
  if (pounds == null || !Number.isFinite(pounds) || pounds <= 0) return null;
  const items = (weightclasses || [])
    .map((wc) => {
      const label = String(wc.Weight != null ? wc.Weight : "").trim();
      const m = label.match(/(\d+(?:\.\d+)?)/);
      const limit = m ? parseFloat(m[1]) : NaN;
      const sort = typeof wc.Sort === "number" ? wc.Sort : Number(wc.Sort) || 0;
      return { label, limit, sort };
    })
    .filter((x) => x.label && Number.isFinite(x.limit));
  if (items.length === 0) return null;
  items.sort((a, b) => a.limit - b.limit || a.sort - b.sort);
  for (const it of items) {
    if (pounds <= it.limit) return it.label;
  }
  return items[items.length - 1].label;
}

export function rowHasBothWrestlersAndTeams(r) {
  if (!r) return false;
  return !!r.winnerName && !!r.winnerTeam && !!r.loserName && !!r.loserTeam;
}

export function mapIkwfResultToMatchType(raw) {
  const s = String(raw || "").trim();
  if (!s) return "Decision";
  const u = s.toUpperCase();
  if (/\bPIN\b/i.test(s) || /^P\./i.test(s) || /^\s*FALL/i.test(s))
    return "Fall";
  if (/^TF\b|^TECH/i.test(s)) return "Technical Fall";
  if (/^MAJ|^M\./i.test(s)) return "Major";
  if (/^DEC|^SV|^TB|^UTB/i.test(s)) return "Decision";
  if (/^DQ\b|^DISQ/i.test(s)) return "Disqualification";
  if (/^FF\b|^FORFEIT/i.test(s)) return "Forfeit";
  if (/^DEF|^INJ|^DEFAULT/i.test(s)) return "Injury Default";
  return "Decision";
}

export function splitFullName(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export function normalizeKeyPart(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function athleteDedupeKey(fullName, school, rankLeague) {
  return `${normalizeKeyPart(fullName)}|${normalizeKeyPart(
    school
  )}|${normalizeKeyPart(rankLeague)}`;
}

export function parseIkwfDate(dateRaw) {
  const s = String(dateRaw || "").trim();
  if (!s) return null;
  let d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    // Keep clock time when present (e.g. datetime cells) so import order matches
    // event chronology within the same calendar day. Stored match dates still
    // normalize to noon in toFirestoreMatchTimestamp.
    return d;
  }
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdy) {
    let mo = parseInt(mdy[1], 10);
    let day = parseInt(mdy[2], 10);
    let yr = parseInt(mdy[3], 10);
    if (yr < 100) yr += yr >= 50 ? 1900 : 2000;
    d = new Date(yr, mo - 1, day, 12, 0, 0, 0);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

export function seasonFromMatchDate(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = d.getMonth();
  if (m >= 6) return `${y}-${String(y + 1).slice(-2)}`;
  return `${y - 1}-${String(y).slice(-2)}`;
}

function toFirestoreMatchTimestamp(d) {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  return Timestamp.fromDate(x);
}

export function ikwfMatchSessionDupKey(
  winnerId,
  loserId,
  matchDate,
  matchType
) {
  const ts = toFirestoreMatchTimestamp(matchDate);
  return `${winnerId}|${loserId}|${ts.toMillis()}|${String(matchType || "")}`;
}

/**
 * True when Firestore is refusing or severely throttling work (quota, limits, overload).
 * Stops batch import so we can save CSV progress. Includes SDK "maximum backoff" logs.
 */
export function isFirestoreQuotaExceededError(err) {
  if (!err) return false;
  const code = String(err.code || "").toLowerCase();
  const raw = String(err.message != null ? err.message : err);
  const msg = raw.toLowerCase();
  if (code === "resource-exhausted") return true;
  if (msg.includes("resource_exhausted") || msg.includes("resource-exhausted"))
    return true;
  if (msg.includes("quota exceeded") || msg.includes("quota_exceeded"))
    return true;
  if (
    msg.includes("exceeded") &&
    (msg.includes("quota") ||
      msg.includes("write") ||
      msg.includes("daily") ||
      msg.includes("limit"))
  )
    return true;
  // Firebase 9.x often logs this when limits are hit; may appear on thrown errors too.
  if (msg.includes("maximum backoff") || msg.includes("backoff delay"))
    return true;
  if (
    msg.includes("prevent overloading") ||
    msg.includes("overloading the backend")
  )
    return true;
  return false;
}

async function canonicalMatchAlreadyExists(db, canonicalId) {
  const ref = doc(db, MATCHES_CANONICAL_COLLECTION, canonicalId);
  const snap = await getDoc(ref);
  return snap.exists();
}

/**
 * Winner beats loser. Updates ELO, wins/losses, writes `matches_canonical` (merge).
 * @returns {{ ok: boolean, skip?: string, error?: string, quotaExceeded?: boolean }}
 */
export async function commitIkwfMatch(db, params) {
  const {
    winnerId,
    loserId,
    eventName,
    matchDate,
    matchType,
    winnerRankWeightClass,
    loserRankWeightClass,
    sessionDupCache,
  } = params;
  try {
    if (!winnerId || !loserId || winnerId === loserId) {
      return { ok: false, skip: "invalid_ids" };
    }
    const wRef = doc(db, "athletes", winnerId);
    const lRef = doc(db, "athletes", loserId);

    if (sessionDupCache) {
      const k = ikwfMatchSessionDupKey(winnerId, loserId, matchDate, matchType);
      if (sessionDupCache.has(k)) {
        return { ok: false, skip: "duplicate" };
      }
    }

    const ts = toFirestoreMatchTimestamp(matchDate);
    const canonicalId = await computeCanonicalMatchDocId(
      winnerId,
      loserId,
      ts,
      matchType
    );
    const dup = await canonicalMatchAlreadyExists(db, canonicalId);
    if (dup) {
      if (winnerRankWeightClass || loserRankWeightClass) {
        const wb = writeBatch(db);
        if (winnerRankWeightClass) {
          wb.update(wRef, { RankWeightClass: winnerRankWeightClass });
        }
        if (loserRankWeightClass) {
          wb.update(lRef, { RankWeightClass: loserRankWeightClass });
        }
        await wb.commit();
      }
      return { ok: false, skip: "duplicate" };
    }

    const [wSnap, lSnap] = await Promise.all([getDoc(wRef), getDoc(lRef)]);
    if (!wSnap.exists() || !lSnap.exists()) {
      return { ok: false, skip: "missing_athlete" };
    }
    const wData = wSnap.data();
    const lData = lSnap.data();
    let wSkill = typeof wData.Skill === "number" ? wData.Skill : 1200;
    let lSkill = typeof lData.Skill === "number" ? lData.Skill : 1200;
    let wWins = typeof wData.Wins === "number" ? wData.Wins : 0;
    let wLosses = typeof wData.Losses === "number" ? wData.Losses : 0;
    let lWins = typeof lData.Wins === "number" ? lData.Wins : 0;
    let lLosses = typeof lData.Losses === "number" ? lData.Losses : 0;

    const wName =
      (wData.FullName && String(wData.FullName).trim()) ||
      `${wData.FirstName || ""} ${wData.LastName || ""}`.trim() ||
      "Wrestler";
    const lName =
      (lData.FullName && String(lData.FullName).trim()) ||
      `${lData.FirstName || ""} ${lData.LastName || ""}`.trim() ||
      "Wrestler";

    const elo = new Elo();
    const newWSkill = elo.ifWins(wSkill, lSkill);
    const newLSkill = elo.ifLoses(lSkill, wSkill);
    let multiFactor = 1.0;
    if (matchType === "Major" || matchType === "Technical Fall") {
      multiFactor = 1.2;
    } else if (matchType === "Fall") {
      multiFactor = 1.3;
    }
    const adjW = wSkill + (newWSkill - wSkill) * multiFactor;
    const adjL = lSkill + (newLSkill - lSkill) * multiFactor;
    const finalW = Math.max(1, adjW);
    const finalL = Math.max(1, adjL);
    const skillChangeW = Math.round((finalW - wSkill) * 100) / 100;
    const skillChangeL = Math.round((finalL - lSkill) * 100) / 100;

    wWins += 1;
    lLosses += 1;

    const season = seasonFromMatchDate(matchDate);
    const location = String(eventName || "").trim() || "IKWF-History import";

    const batch = writeBatch(db);
    const canonicalRef = doc(db, MATCHES_CANONICAL_COLLECTION, canonicalId);
    batch.set(
      canonicalRef,
      {
        WinnerAthleteId: winnerId,
        LoserAthleteId: loserId,
        ParticipantAthleteIds: participantAthleteIds(winnerId, loserId),
        MatchDate: ts,
        Season: season,
        Type: matchType,
        Location: location,
        VideoLink: "",
        LastMigratedAt: serverTimestamp(),
        LegacySourcePaths: arrayUnion("ikwf-history-csv-import"),
        SkillChangeWinner: skillChangeW,
        SkillChangeLoser: skillChangeL,
        OpponentLabelWinnerDoc: lName,
        OpponentLabelLoserDoc: wName,
      },
      { merge: true }
    );
    batch.update(wRef, {
      Skill: finalW,
      Wins: wWins,
      Losses: wLosses,
      ...(winnerRankWeightClass
        ? { RankWeightClass: winnerRankWeightClass }
        : {}),
    });
    batch.update(lRef, {
      Skill: finalL,
      Wins: lWins,
      Losses: lLosses,
      ...(loserRankWeightClass
        ? { RankWeightClass: loserRankWeightClass }
        : {}),
    });
    await batch.commit();
    if (sessionDupCache) {
      sessionDupCache.add(
        ikwfMatchSessionDupKey(winnerId, loserId, matchDate, matchType)
      );
    }
    return { ok: true };
  } catch (e) {
    const msg = e && e.message ? e.message : String(e);
    return {
      ok: false,
      error: msg,
      quotaExceeded: isFirestoreQuotaExceededError(e),
    };
  }
}
